import {
  Stack,
  Group,
  NumberInput,
  Button,
  FileInput,
  Text,
  ActionIcon,
  Badge,
  SegmentedControl,
  SimpleGrid,
  Paper,
} from "@mantine/core";
import {
  IconTrash,
  IconUpload,
  IconQrcode,
  IconLetterCase,
  IconSquare,
} from "@tabler/icons-react";
import {
  SIZE_PRESETS,
  FIELD_ELEMENTS,
  elementDisplayName,
  convertSize,
} from "./badgeConstants";

// Sidebar shown when no element is selected: size, stock artwork, element
// palette and the list of already-added elements.
export default function BadgePalette({
  size,
  setSize,
  bgFile,
  bgError,
  onBgUpload,
  hasStock,
  backgroundFit,
  setBackgroundFit,
  elements,
  selectedId,
  setSelectedId,
  addElement,
  removeElement,
}) {
  const isPreset = (preset) =>
    size.unit === preset.unit &&
    size.width === preset.width &&
    size.height === preset.height;

  return (
    <>
      <Stack gap="xs">
        <Text size="sm" fw={600}>
          Badge Size
        </Text>

        <Group gap="xs">
          {SIZE_PRESETS.map((preset) => (
            <Badge
              key={preset.label}
              size="lg"
              variant={isPreset(preset) ? "filled" : "outline"}
              style={{ cursor: "pointer" }}
              onClick={() =>
                setSize({
                  width: preset.width,
                  height: preset.height,
                  unit: preset.unit,
                })
              }
            >
              {preset.label}
            </Badge>
          ))}
        </Group>

        <Group grow>
          <NumberInput
            label="Width"
            value={size.width}
            onChange={(val) =>
              setSize((prev) => ({ ...prev, width: val === "" ? "" : val }))
            }
            min={0.1}
            decimalScale={2}
          />
          <NumberInput
            label="Height"
            value={size.height}
            onChange={(val) =>
              setSize((prev) => ({ ...prev, height: val === "" ? "" : val }))
            }
            min={0.1}
            decimalScale={2}
          />
        </Group>

        <SegmentedControl
          fullWidth
          size="xs"
          value={size.unit}
          onChange={(val) => setSize((prev) => convertSize(prev, val))}
          data={[
            { label: "Inches", value: "in" },
            { label: "Millimeters", value: "mm" },
          ]}
        />
      </Stack>

      <Stack gap="xs">
        <Text size="sm" fw={600}>
          Stock
        </Text>
        <FileInput
          placeholder="Upload stock artwork"
          accept="application/pdf,image/png,image/jpeg,image/svg+xml"
          leftSection={<IconUpload size={16} />}
          value={bgFile}
          onChange={onBgUpload}
          clearable
          error={bgError}
          description="Used as a guide only. Your pre-printed badge supplies this artwork; it is not sent to the printer."
          inputWrapperOrder={["label", "input", "description", "error"]}
        />
        {hasStock && (
          <SegmentedControl
            fullWidth
            size="xs"
            value={backgroundFit}
            onChange={setBackgroundFit}
            data={[
              { label: "Fit", value: "fit" },
              { label: "Fill", value: "fill" },
            ]}
          />
        )}
      </Stack>

      <Stack gap="xs">
        <Text size="sm" fw={600}>
          Attendee Fields
        </Text>
        <SimpleGrid cols={2} spacing="xs">
          {FIELD_ELEMENTS.map((f) => (
            <Button
              key={f.fieldKey}
              variant="light"
              size="xs"
              onClick={() => addElement("field", f.fieldKey)}
            >
              {f.label}
            </Button>
          ))}
        </SimpleGrid>
      </Stack>

      <Stack gap="xs">
        <Text size="sm" fw={600}>
          Elements
        </Text>
        <SimpleGrid cols={3} spacing="xs">
          <Button
            variant="light"
            size="xs"
            leftSection={<IconQrcode size={16} />}
            onClick={() => addElement("qr")}
          >
            QR
          </Button>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconLetterCase size={16} />}
            onClick={() => addElement("text")}
          >
            Text
          </Button>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconSquare size={16} />}
            onClick={() => addElement("rectangle")}
          >
            Box
          </Button>
        </SimpleGrid>
      </Stack>

      {elements.length > 0 && (
        <Stack gap="xs" mt="sm">
          <Text size="sm" fw={600}>
            Added Elements
          </Text>
          <Stack gap={4}>
            {elements.map((el) => (
              <Paper
                key={el.id}
                withBorder
                p="xs"
                radius="md"
                onClick={() => setSelectedId(el.id)}
                style={{
                  cursor: "pointer",
                  backgroundColor: selectedId === el.id ? "#e7f5ff" : "white",
                }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Text size="sm" fw={500} lineClamp={1}>
                    {elementDisplayName(el)}
                  </Text>
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={(e) => removeElement(el.id, e)}
                    size="sm"
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Stack>
      )}
    </>
  );
}
